import React, { useEffect, useRef, useState } from 'react';
import {
  ApplicationAlert,
  AlertStatus,
  AlertType,
  EvaluationListItemDto,
} from '../../types/admin';
import { Evaluator } from '../../types/evaluator';
import adminService from '../../services/adminService';
import Button from 'shared/components/UC/Button';
import styles from './ApplicationListTable.module.css';
import thirdStyles from './ThirdEvaluatorModal.module.css';

interface AlertActionsMenuProps {
  alert: ApplicationAlert;
  onResolve: (alertId: string, resolutionNote: string) => void;
  onDismiss: (alertId: string, note: string) => void;
}

const AlertActionsMenu: React.FC<AlertActionsMenuProps> = ({
  alert,
  onResolve,
  onDismiss,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [showDismissModal, setShowDismissModal] = useState(false);
  const [showResolutionModal, setShowResolutionModal] = useState(false);
  const [showDismissalModal, setShowDismissalModal] = useState(false);
  const [showThirdModal, setShowThirdModal] = useState(false);
  const [resolutionNote, setResolutionNote] = useState('');
  const [dismissNote, setDismissNote] = useState('');

  // Third-evaluator modal state
  const [thirdLoading, setThirdLoading] = useState(false);
  const [thirdSubmitting, setThirdSubmitting] = useState(false);
  const [thirdError, setThirdError] = useState('');
  const [evaluationScores, setEvaluationScores] = useState<
    EvaluationListItemDto[]
  >([]);
  const [availableEvaluators, setAvailableEvaluators] = useState<Evaluator[]>(
    []
  );
  const [, setAssignedEvaluatorIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEvaluatorId, setSelectedEvaluatorId] = useState('');

  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updatePosition = () => {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setDropdownPosition({ top: rect.bottom, left: rect.right });
      }
    };
    if (isOpen) {
      updatePosition();
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
    }
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        !buttonRef.current?.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const openThirdModal = async () => {
    setIsOpen(false);
    setThirdError('');
    setSearchQuery('');
    setSelectedEvaluatorId('');
    setShowThirdModal(true);
    setThirdLoading(true);
    try {
      const appId = alert.applicationId ?? '';
      const [evalsResp, allEvaluators, assignments] = await Promise.all([
        adminService.getApplicationEvaluations(appId),
        adminService.getAvailableEvaluators(),
        adminService.getApplicationEvaluators(appId),
      ]);
      const alreadyAssigned = new Set(assignments.map((a) => a.evaluatorId));
      setEvaluationScores(
        evalsResp.evaluations.filter((e) => e.finalScore !== null)
      );
      setAssignedEvaluatorIds(alreadyAssigned);
      setAvailableEvaluators(
        allEvaluators.filter((e) => !alreadyAssigned.has(e.id))
      );
    } catch {
      setThirdError('No se pudo cargar la información. Intente nuevamente.');
    } finally {
      setThirdLoading(false);
    }
  };

  const handleThirdSubmit = async () => {
    if (!selectedEvaluatorId) return;
    setThirdSubmitting(true);
    setThirdError('');
    try {
      await adminService.assignEvaluator(alert.applicationId ?? '', {
        evaluatorId: selectedEvaluatorId,
      });
      const evaluator = availableEvaluators.find(
        (e) => e.id === selectedEvaluatorId
      );
      onResolve(
        alert.id,
        `Tercer evaluador asignado: ${evaluator?.name ?? selectedEvaluatorId}`
      );
      setShowThirdModal(false);
    } catch (err) {
      setThirdError(
        err instanceof Error ? err.message : 'Error al asignar el evaluador.'
      );
    } finally {
      setThirdSubmitting(false);
    }
  };

  const handleResolveSubmit = () => {
    onResolve(alert.id, resolutionNote);
    setShowResolveModal(false);
    setResolutionNote('');
  };

  const formatDate = (date: Date | string) => {
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const canResolve = alert.status === AlertStatus.OPEN;
  const canDismiss = alert.status === AlertStatus.OPEN;
  const canViewResolution = alert.status === AlertStatus.RESOLVED;
  const canViewDismissal = alert.status === AlertStatus.DISMISSED;
  const isScoreVariance =
    alert.alertType === AlertType.EVALUATION_SCORE_VARIANCE;

  if (!canResolve && !canDismiss && !canViewResolution && !canViewDismissal) {
    return <span className={styles.noActions}>—</span>;
  }

  const scoreVariance =
    evaluationScores.length >= 2
      ? Math.max(...evaluationScores.map((e) => e.finalScore!)) -
        Math.min(...evaluationScores.map((e) => e.finalScore!))
      : null;

  const filteredEvaluators = availableEvaluators.filter(
    (e) =>
      e.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      e.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const actions: { label: string; handler: () => void }[] = [];
  if (canResolve && isScoreVariance)
    actions.push({
      label: 'Seleccionar tercer evaluador',
      handler: openThirdModal,
    });
  if (canResolve)
    actions.push({
      label: 'Marcar como resuelta',
      handler: () => setShowResolveModal(true),
    });
  if (canDismiss)
    actions.push({
      label: 'Descartar',
      handler: () => setShowDismissModal(true),
    });
  if (canViewResolution)
    actions.push({
      label: 'Ver nota',
      handler: () => setShowResolutionModal(true),
    });
  if (canViewDismissal)
    actions.push({
      label: 'Ver nota',
      handler: () => setShowDismissalModal(true),
    });

  const buttonElement =
    actions.length === 1 ? (
      <Button
        variant="primary"
        size="sm"
        className={styles.actionButton}
        onClick={actions[0].handler}
      >
        {actions[0].label}
      </Button>
    ) : (
      <div className={styles.actionsContainer} ref={buttonRef}>
        <Button
          variant="primary"
          size="sm"
          onClick={() => setIsOpen(!isOpen)}
          className={styles.actionButton}
        >
          Acciones
          <i
            className="uc-icon icon-size--sm"
            style={{ marginLeft: '0.25rem' }}
          >
            expand_more
          </i>
        </Button>
        {isOpen && dropdownPosition && (
          <div
            ref={menuRef}
            className={styles.actionsMenuDropdown}
            style={{
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              transform: 'translateX(-100%)',
            }}
          >
            {actions.map((a) => (
              <button
                key={a.label}
                onClick={() => {
                  setIsOpen(false);
                  a.handler();
                }}
                className={styles.actionsMenuItem}
              >
                {a.label}
              </button>
            ))}
          </div>
        )}
      </div>
    );

  return (
    <>
      {buttonElement}

      {/* Third Evaluator Modal */}
      {showThirdModal && (
        <div
          className={styles.modalOverlay}
          onClick={() => !thirdSubmitting && setShowThirdModal(false)}
        >
          <div
            className={`${styles.modal} ${thirdStyles.wideModal}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <h3>Seleccionar tercer evaluador</h3>
              {alert.application?.applicationNumber && (
                <p className={thirdStyles.modalSubtitle}>
                  Postulación:{' '}
                  <strong>{alert.application.applicationNumber}</strong>
                </p>
              )}
            </div>

            <div
              className={`${styles.modalBody} ${thirdStyles.modalBodyScrollable}`}
            >
              {thirdLoading ? (
                <p className={thirdStyles.loading}>Cargando información...</p>
              ) : (
                <>
                  {/* Context: current scores */}
                  <div className={thirdStyles.section}>
                    <h4 className={thirdStyles.sectionTitle}>
                      Puntajes actuales
                    </h4>
                    {evaluationScores.length === 0 ? (
                      <p className={thirdStyles.emptyNote}>
                        No hay evaluaciones con puntaje enviado aún.
                      </p>
                    ) : (
                      <>
                        {scoreVariance !== null && (
                          <div className={thirdStyles.varianceCallout}>
                            <span className={thirdStyles.varianceLabel}>
                              Variación detectada
                            </span>
                            <span className={thirdStyles.varianceValue}>
                              {scoreVariance.toFixed(2)} puntos
                            </span>
                          </div>
                        )}
                        <table className={thirdStyles.scoresTable}>
                          <thead>
                            <tr>
                              <th>Evaluador</th>
                              <th>Puntaje</th>
                            </tr>
                          </thead>
                          <tbody>
                            {evaluationScores.map((ev, i) => (
                              <tr key={i}>
                                <td>{ev.evaluatorName}</td>
                                <td className={thirdStyles.scoreCell}>
                                  {ev.finalScore !== null
                                    ? ev.finalScore.toFixed(2)
                                    : '—'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </>
                    )}
                  </div>

                  {/* Evaluator picker */}
                  <div className={thirdStyles.section}>
                    <h4 className={thirdStyles.sectionTitle}>
                      Evaluador a asignar
                    </h4>
                    <input
                      type="text"
                      className={thirdStyles.searchInput}
                      placeholder="Buscar por nombre o correo..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <div className={thirdStyles.evaluatorList}>
                      {filteredEvaluators.length === 0 ? (
                        <p className={thirdStyles.emptyNote}>
                          {searchQuery
                            ? 'Sin resultados para la búsqueda.'
                            : 'No hay evaluadores disponibles.'}
                        </p>
                      ) : (
                        filteredEvaluators.map((ev) => (
                          <label
                            key={ev.id}
                            className={thirdStyles.evaluatorRow}
                          >
                            <input
                              type="radio"
                              name="third-evaluator"
                              value={ev.id}
                              checked={selectedEvaluatorId === ev.id}
                              onChange={() => setSelectedEvaluatorId(ev.id)}
                            />
                            <span className={thirdStyles.evaluatorInfo}>
                              <span className={thirdStyles.evaluatorName}>
                                {ev.name}
                              </span>
                              <span className={thirdStyles.evaluatorEmail}>
                                {ev.email}
                              </span>
                            </span>
                            {ev.isExternal && (
                              <span className={thirdStyles.externalBadge}>
                                Externo
                              </span>
                            )}
                          </label>
                        ))
                      )}
                    </div>
                  </div>

                  {thirdError && (
                    <p className={thirdStyles.error}>{thirdError}</p>
                  )}
                </>
              )}
            </div>

            <div className={styles.modalActions}>
              <Button
                variant="secondary"
                onClick={() => setShowThirdModal(false)}
                disabled={thirdSubmitting}
              >
                Cancelar
              </Button>
              <Button
                variant="primary"
                onClick={handleThirdSubmit}
                disabled={
                  !selectedEvaluatorId || thirdLoading || thirdSubmitting
                }
              >
                {thirdSubmitting ? 'Asignando...' : 'Asignar evaluador'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Resolve Modal */}
      {showResolveModal && (
        <div
          className={styles.modalOverlay}
          onClick={() => {
            setShowResolveModal(false);
            setResolutionNote('');
          }}
        >
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Marcar Alerta como resuelta</h3>
            </div>
            <div className={styles.modalBody}>
              <p>
                <strong>Alerta:</strong> {alert.title}
              </p>
              <div className={styles.formGroup}>
                <label htmlFor="resolutionNote">
                  Nota de resolución (obligatoria):
                </label>
                <textarea
                  id="resolutionNote"
                  value={resolutionNote}
                  onChange={(e) => setResolutionNote(e.target.value)}
                  rows={4}
                  placeholder="Describe cómo se resolvió la alerta..."
                  className={styles.textarea}
                />
              </div>
            </div>
            <div className={styles.modalActions}>
              <Button
                variant="secondary"
                onClick={() => {
                  setShowResolveModal(false);
                  setResolutionNote('');
                }}
              >
                Cancelar
              </Button>
              <Button
                variant="primary"
                onClick={handleResolveSubmit}
                disabled={!resolutionNote.trim()}
              >
                Marcar como resuelta
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Dismiss Modal */}
      {showDismissModal && (
        <div
          className={styles.modalOverlay}
          onClick={() => {
            setShowDismissModal(false);
            setDismissNote('');
          }}
        >
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Descartar alerta</h3>
            </div>
            <div className={styles.modalBody}>
              <p>
                <strong>{alert.title}</strong>
              </p>
              <div className={styles.formGroup}>
                <label htmlFor="dismissNote">
                  Justificación (obligatoria):
                </label>
                <textarea
                  id="dismissNote"
                  value={dismissNote}
                  onChange={(e) => setDismissNote(e.target.value)}
                  rows={3}
                  placeholder="Indica el motivo por el que se descarta esta alerta..."
                  className={styles.textarea}
                />
              </div>
            </div>
            <div className={styles.modalActions}>
              <Button
                variant="secondary"
                onClick={() => {
                  setShowDismissModal(false);
                  setDismissNote('');
                }}
              >
                Cancelar
              </Button>
              <Button
                variant="primary"
                disabled={!dismissNote.trim()}
                onClick={() => {
                  onDismiss(alert.id, dismissNote);
                  setShowDismissModal(false);
                  setDismissNote('');
                }}
              >
                Descartar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Dismissal note view modal */}
      {showDismissalModal && (
        <div
          className={styles.modalOverlay}
          onClick={() => setShowDismissalModal(false)}
        >
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Nota de descarte</h3>
            </div>
            <div className={styles.modalBody}>
              <p>
                <strong>Alerta:</strong> {alert.title}
              </p>
              <div className={styles.formGroup}>
                <label>Justificación:</label>
                <div className={styles.resolutionContent}>
                  {alert.dismissNote || 'No se proporcionó justificación.'}
                </div>
              </div>
              <div className={styles.formGroup}>
                <label>Descartada por:</label>
                <div className={styles.resolutionMeta}>
                  {alert.resolvedByUser?.name || 'Usuario desconocido'}
                </div>
              </div>
              <div className={styles.formGroup}>
                <label>Fecha:</label>
                <div className={styles.resolutionMeta}>
                  {alert.resolvedAt ? formatDate(alert.resolvedAt) : '—'}
                </div>
              </div>
            </div>
            <div className={styles.modalActions}>
              <Button
                variant="primary"
                onClick={() => setShowDismissalModal(false)}
              >
                Cerrar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Resolution View Modal */}
      {showResolutionModal && (
        <div
          className={styles.modalOverlay}
          onClick={() => setShowResolutionModal(false)}
        >
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Detalles de Resolución</h3>
            </div>
            <div className={styles.modalBody}>
              <p>
                <strong>Alerta:</strong> {alert.title}
              </p>
              <div className={styles.formGroup}>
                <label>Nota de resolución:</label>
                <div className={styles.resolutionContent}>
                  {alert.resolutionNote ||
                    'No se proporcionó una nota de resolución.'}
                </div>
              </div>
              <div className={styles.formGroup}>
                <label>Resuelto por:</label>
                <div className={styles.resolutionMeta}>
                  {alert.resolvedByUser?.name || 'Usuario desconocido'}
                </div>
              </div>
              <div className={styles.formGroup}>
                <label>Fecha de resolución:</label>
                <div className={styles.resolutionMeta}>
                  {alert.resolvedAt
                    ? formatDate(alert.resolvedAt)
                    : 'Fecha desconocida'}
                </div>
              </div>
            </div>
            <div className={styles.modalActions}>
              <Button
                variant="primary"
                onClick={() => setShowResolutionModal(false)}
              >
                Cerrar
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AlertActionsMenu;
